<template>
	<Transition @enter="enter" @leave="leave">
		<div v-if="state.visibleUserMenu" ref="bgRef" class="fixed inset-0 bg-background-dark/25 z-popup backdrop-blur-[.1rem] flex justify-end">
			<nav ref="navRef" class="bg-background-primary rem:w-[287px] overflow-hidden flex flex-col">
				<div class="flex px-5 py-6 gap-4 items-center">
					<h4 class="font-bold font-secondary text-base text-heading-primary text-md flex-1 text-right">
						{{ profile?.personalInfo?.name }} {{ profile?.personalInfo?.surname }}
					</h4>
					<button type="button" class="flex-0 focus-outline" @click="handleClose">
						<NuxtIcon name="x" class="text-[1.75rem] text-icon-primary" />
					</button>
				</div>
				<div class="p-4 overflow-y-auto">
					<AbsoluteInternalLink
						v-for="(item, index) in menuLinks"
						:key="index"
						:to="item.to"
						class="text-base text-body-secondary py-3 px-2.5 flex gap-2 items-center hover:bg-button-secondary-surface-hover w-full group [&.router-link-active]:text-body-primary rounded-lg"
						@click="cta(item.value)"
					>
						<NuxtIcon :name="item.icon" class="text-body-secondary/60 text-sm group-[.router-link-active]:text-icon-primary" filled />
						{{ item.value }}
					</AbsoluteInternalLink>
					<CrmRedirectButton
						:user-state="UserState.UPDATE_PROFILE"
						class="text-base text-body-secondary py-3 px-2.5 flex gap-2 items-center hover:bg-button-secondary-surface-hover w-full group [&.router-link-active]:text-body-primary rounded-lg"
					>
						<NuxtIcon name="settings" class="text-body-secondary/60 text-sm" filled />
						Editace Můj Globus účtu
					</CrmRedirectButton>
					<hr class="border-border-secondary !my-4" />
					<button
						type="button"
						class="text-base text-body-secondary py-3 px-2.5 flex gap-2 items-center hover:bg-button-secondary-surface-hover w-full group [&.router-link-active]:text-body-primary rounded-lg"
						@click="logOut"
					>
						<NuxtIcon name="log-out" class="text-body-secondary/60 text-base" filled />
						Odhlásit se
					</button>
				</div>
			</nav>
		</div>
	</Transition>
</template>

<script setup lang="ts">
	import {gsap} from 'gsap';
	import {storeToRefs} from 'pinia';
	import {useGtm} from '@gtm-support/vue-gtm';
	import type {RouteLocationRaw} from 'vue-router';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import type {Header} from '../../types/header';
	import {useCustomerProfileStore} from '../../stores/customerProfile';
	import {UserState} from '~/stores/crmStateStore';

	type Props = {
		model: Header
	};

	type Link = {
		to: RouteLocationRaw
		icon: string
		value: string
	};

	const branchSpecificPath = useBranchSpecificPath();

	const menuLinks = ref<Link[]>([
		{
			to: {name: 'muj-ucet'},
			icon: 'user',
			value: 'Můj Globus účet',
		},
		{
			to: {name: 'muj-ucet-kupony'},
			icon: 'custom/sale',
			value: 'Moje odměny a kupony',
		},
		{
			to: {name: 'muj-ucet-prehled-uctenek'},
			icon: 'custom/receipts',
			value: 'Přehled mých účtenek',
		},
		{
			to: {name: 'nakupni-seznamy'},
			icon: 'shopping-list',
			value: 'Nákupní seznamy',
		},
		{
			to: branchSpecificPath({name: 'hypermarket-akcni-nabidka-category', params: {category: 'pro-vas'}}).value,
			icon: 'custom/weight',
			value: 'Nabídka Pro Vás',
		},
	]);

	const props = defineProps<Props>();
	const bgRef = ref<HTMLElement | null>(null);
	const navRef = ref<HTMLElement | null>(null);

	const gtm = useGtm();
	const state = useVModel(props, 'model');
	const customerProfileStore = useCustomerProfileStore();
	const {userIsLogged, profile} = storeToRefs(customerProfileStore);

	const {signOut} = useSessionActions();
	const isLocked = useBodyScrollLock();

	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(bgRef, {initialFocus: false});

	function enter(el: Element, onComplete: () => void) {
		gsap.timeline({onComplete}).fromTo(el,
			{opacity: 0},
			{opacity: 1, duration: 0.2},
		).fromTo(el.children,
			{xPercent: 100},
			{xPercent: 0, duration: 0.3},
		);
	}

	function leave(el: Element, onComplete: () => void) {
		gsap.timeline({onComplete}).fromTo(el.children,
			{xPercent: 0},
			{xPercent: 100, duration: 0.2},
		).to(el,
			{opacity: 0, duration: 0.2},
		);
	}

	function closeMenu() {
		state.value.visibleUserMenu = false;
	}

	function logOut() {
		signOut({});
		closeMenu();
	}

	function cta(value: string) {
		gtm?.push({
			event: 'user_menu_click',
			content: 'text',
			text: value,
			page_type: getGtmPageType(),
		});
		closeMenu();
	}

	function handleClose() {
		closeMenu();
		trackHeaderEvent('close_icon_rightsite');
	}

	onClickOutside(navRef, () => { state.value.visibleUserMenu = false; });
	onKeyStroke('Escape', closeMenu);

	watch(userIsLogged, () => {
		if (!userIsLogged.value) {
			state.value.visibleUserMenu = false;
		}
	});

	watch(() => state.value.visibleUserMenu, async (newValue) => {
		isLocked.value = newValue;
		await nextTick();
		newValue ? activateFocusTrap() : deactivateFocusTrap();
	}, {immediate: true});
</script>
